import { hashKey } from '@tanstack/react-query';
import type { QueryState } from '@tanstack/react-query';

import { queryClient } from '@/utils/query/queryClient';

import projectCreditsQueryKey from './projectCreditsQueryKey';

interface WatchProjectCreditsParams {
  flowId: string;
  /**
   * When the caller started waiting (ms epoch). A failure from before it is a
   * stale leftover the next mount refetches; one from after it is final, even
   * if it landed before this watch was installed.
   */
  since: number;
  /** The quote, or `null` once the request has failed for good. */
  onResult: (credits: number | null) => void;
}

/**
 * Hands over a flow's calculator quote from the `useProjectCredits` cache
 * without ever asking for it: a settled quote is delivered at once, otherwise
 * the call waits for the request a mounted `useProjectCredits` makes. Nothing
 * is delivered while no consumer asks (guest, free preset, hidden feed cost).
 *
 * Returns the unsubscribe; calling it after delivery is a no-op.
 */
const watchProjectCredits = ({
  flowId,
  since,
  onResult,
}: WatchProjectCreditsParams): (() => void) => {
  const queryKey = projectCreditsQueryKey(flowId);

  // `undefined` while the quote is still open.
  const readResult = (
    state: QueryState<unknown> | undefined
  ): number | null | undefined => {
    if (state?.status === 'success') return (state.data as number) ?? null;
    if (
      state?.status === 'error' &&
      state.fetchStatus === 'idle' &&
      state.errorUpdatedAt >= since
    ) {
      return null;
    }
    return undefined;
  };

  const settled = readResult(queryClient.getQueryState(queryKey));
  if (settled !== undefined) {
    onResult(settled);
    return () => {};
  }

  const queryHash = hashKey(queryKey);
  const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
    if (event.type !== 'updated' || event.query.queryHash !== queryHash) return;
    const result = readResult(event.query.state);
    if (result === undefined) return;
    unsubscribe();
    onResult(result);
  });
  return unsubscribe;
};

export default watchProjectCredits;
